import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import axios from 'axios';
import { API_URL } from '../config';

const CertificateVerification = () => {
  const { id } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/certificate/${id}`, {
          timeout: 15000,
        });
        setCertificate(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Certificate not found. Please check the QR code and try again.');
        } else {
          setError('Error fetching certificate details. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} thickness={4} sx={{ color: '#3B82F6' }} />
        <Typography variant="h6" color="text.secondary">Verifying Certificate...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', p: 2 }}>
        <Container maxWidth="md">
          <Paper className="glass-card" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Alert severity="error" variant="filled" sx={{ borderRadius: 2, fontSize: '1.1rem' }}>
              {error}
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  const tableData = Object.entries(certificate)
    .filter(([key]) => !['certificateId', 'createdAt', 'logos'].includes(key))
    .map(([key, value]) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').replace(/_/g, ' '),
      value: value
    }));

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 4, md: 8 }, px: 2 }}>
      <Container maxWidth="md">
        <Paper
          className="glass-card"
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Top accent line */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '8px',
              background: 'linear-gradient(90deg, #10B981 0%, #3B82F6 100%)',
            }}
          />

          {/* Verification Badge */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 5, mt: 2 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#ECFDF5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)',
                border: '4px solid #fff'
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 40, color: '#10B981' }} />
            </Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, color: '#0F172A', textAlign: 'center', mb: 1 }}>
              Verified Certificate
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkspacePremiumIcon fontSize="small" /> Official Credential Record
            </Typography>
          </Box>

          <Divider sx={{ mb: 5, borderStyle: 'dashed' }} />

          {/* Student/Certificate Details */}
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {tableData.map((row, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: 'rgba(248, 250, 252, 0.6)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: '#fff',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                      borderColor: '#CBD5E1'
                    }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#0F172A', mt: 0.5, fontWeight: 600, wordBreak: 'break-word' }}>
                    {row.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Partner Logos */}
          {certificate.logos && certificate.logos.length > 0 && (
            <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
              <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mb: 3, fontWeight: 600, letterSpacing: 2 }}>
                Authorized By
              </Typography>
              <Grid container spacing={4} justifyContent="center" alignItems="center">
                {certificate.logos.map((logo, index) => (
                  <Grid item key={index}>
                    <Box
                      component="img"
                      src={logo}
                      alt={`Partner logo ${index + 1}`}
                      sx={{
                        maxHeight: 70,
                        maxWidth: 180,
                        objectFit: 'contain',
                        filter: 'grayscale(20%)',
                        transition: 'all 0.3s',
                        '&:hover': { filter: 'grayscale(0%)', transform: 'scale(1.05)' }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Certificate ID: {certificate.certificateId} <br/>
              Issued: {new Date(certificate.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CertificateVerification;